import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { examAPI } from '../services/api';
import {
  submitExamStart,
  submitExamSuccess,
  submitExamFailure,
} from '../store/slices/examSlice';

const TakeExam = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // State management
  const [examData, setExamData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const examDataRef = useRef(null);

  // Initialize exam
  useEffect(() => {
    const initializeExam = async () => {
      try {
        setLoading(true);
        setError(null);

        // Start the exam
        const response = await examAPI.startExam(examId);
        
        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Failed to start exam');
        }

        const exam = response.data.data;
        
        // Validate exam data
        if (!exam?.questions?.length || !exam?.duration || !exam?.attemptId) {
          throw new Error('Invalid exam data received');
        }

        // Set exam data and initialize timer
        setExamData(exam);
        setTimeLeft(exam.duration * 60);
        examDataRef.current = exam;
        setLoading(false);
      } catch (err) {
        console.error('Error starting exam:', err);
        setError(err.message || 'Failed to start exam');
        setLoading(false);
      }
    };

    initializeExam();
  }, [examId]);

  // Timer effect
  useEffect(() => {
    if (!timeLeft) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Handle answer selection
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Handle exam submission
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      // Format answers in the correct structure
      const formattedAnswers = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
        questionId: parseInt(questionId),
        selectedAnswer: selectedAnswer
      }));

      // Ensure we have all required data
      if (!examDataRef.current?.attemptId) {
        throw new Error('Exam attempt ID is missing');
      }

      if (formattedAnswers.length === 0) {
        throw new Error('No answers provided');
      }

      console.log('Submitting exam with data:', {
        attemptId: examDataRef.current.attemptId,
        answers: formattedAnswers
      });

      // Submit exam
      const response = await examAPI.submitExam({
        attemptId: examDataRef.current.attemptId,
        answers: formattedAnswers
      });

      if (response.data.success) {
        // Navigate to results page
        navigate('/results');
      } else {
        throw new Error(response.data.message || 'Failed to submit exam');
      }
    } catch (err) {
      console.error('Error submitting exam:', err);
      setError(err.response?.data?.message || err.message || 'Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading exam...
        </Typography>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/dashboard')}
          >
            Return to Dashboard
          </Button>
          {error.includes('already taken') && (
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={() => navigate(`/results/${examId}`)}
            >
              View Results
            </Button>
          )}
        </Box>
      </Container>
    );
  }

  // No exam data state
  if (!examData?.questions) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Exam data is not available. Please try again.</Alert>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => navigate('/dashboard')}
          >
            Return to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  // Main exam view
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4">{examData.title}</Typography>
          <Typography variant="h6" color="primary">
            Time Left: {formatTime(timeLeft)}
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          {examData.description}
        </Typography>

        <Box sx={{ mt: 4 }}>
          {examData.questions.map((question, index) => (
            <Paper key={question.id} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Question {index + 1}
              </Typography>
              <Typography variant="body1" paragraph>
                {question.question_text}
              </Typography>
              <FormControl component="fieldset">
                <FormLabel component="legend">Select your answer:</FormLabel>
                <RadioGroup
                  value={answers[question.id] || ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                >
                  {question.options.map((option) => (
                    <FormControlLabel
                      key={option.letter}
                      value={option.letter}
                      control={<Radio />}
                      label={option.text}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Paper>
          ))}
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={Object.keys(answers).length !== examData.questions.length}
          >
            Submit Exam
          </Button>
        </Box>
      </Paper>

      <Dialog 
        open={showConfirmDialog} 
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to submit your exam? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TakeExam;